import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Search,
  Download,
  FileArchive,
  RotateCcw,
  Eye,
  Pencil,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/project/integrations/supabase/client";
import { Shell, PageHeader } from "@/project/components/Shell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { TablePager } from "@/project/components/TablePager";
import { inr, shortDate, STATUSES } from "@/project/lib/format";
import { categoriesQuery } from "@/project/lib/queries";
import { toCsv, downloadBlob, downloadZip } from "@/project/lib/report-export";

export const Route = createFileRoute("/applications/")({
  head: () => ({
    meta: [
      { title: "All Applications · Assistance & Letter Desk" },
      {
        name: "description",
        content:
          "Browse, search, filter, export and page through every financial assistance application recorded by the trust office.",
      },
      { property: "og:title", content: "All Assistance Applications" },
      {
        property: "og:description",
        content:
          "Search, filter and export every recorded financial assistance application.",
      },
    ],
  }),
  component: ApplicationsPage,
});

const HEAD = [
  "Ref No",
  "Date",
  "Applicant",
  "Beneficiary",
  "Ration Card",
  "Mobile",
  "Category",
  "Purpose",
  "Total Requirement",
  "Applicant Contribution",
  "Trust Contribution",
  "Other Received",
  "Balance Required",
  "Status",
];

function ApplicationsPage() {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [category, setCategory] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [minAmt, setMinAmt] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [viewId, setViewId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const { data: categories } = useQuery(categoriesQuery);

  const { data: apps, isLoading } = useQuery({
    queryKey: ["applications-list"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("applications")
        .select("*, assistance_categories(name)")
        .order("application_date", { ascending: false })
        .limit(1000);
      if (error) {
        throw error;
      }
      return data;
    },
  });

  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    const min = Number(minAmt) || 0;
    return (apps ?? []).filter(a => {
      if (status !== "all" && a.status !== status) {
        return false;
      }
      if (category !== "all" && a.category_id !== category) {
        return false;
      }
      if (from && a.application_date < from) {
        return false;
      }
      if (to && a.application_date > to) {
        return false;
      }
      if (min && Number(a.total_requirement ?? 0) < min) {
        return false;
      }
      if (!term) {
        return true;
      }
      return [
        a.ref_no,
        a.applicant_name,
        a.beneficiary_name,
        a.ration_card_no,
        a.applicant_mobile,
      ]
        .filter(Boolean)
        .some(v => String(v).toLowerCase().includes(term));
    });
  }, [apps, q, status, category, from, to, minAmt]);

  useEffect(() => {
    setPage(1);
  }, [q, status, category, from, to, minAmt, pageSize]);

  const paged = useMemo(
    () => rows.slice((page - 1) * pageSize, page * pageSize),
    [rows, page, pageSize]
  );

  const totals = useMemo(
    () => ({
      requirement: rows.reduce(
        (s, a) => s + Number(a.total_requirement ?? 0),
        0
      ),
      balance: rows.reduce((s, a) => s + Number(a.balance_required ?? 0), 0),
    }),
    [rows]
  );

  const csv = () =>
    toCsv(
      HEAD,
      rows.map(a => [
        a.ref_no,
        a.application_date,
        a.applicant_name,
        a.beneficiary_name,
        a.ration_card_no,
        a.applicant_mobile,
        a.assistance_categories?.name,
        a.purpose,
        a.total_requirement,
        a.applicant_contribution,
        a.our_contribution,
        a.other_received,
        a.balance_required,
        a.status,
      ])
    );

  const stamp = new Date().toISOString().slice(0, 10);

  const exportCsv = () =>
    downloadBlob(
      new Blob([csv()], { type: "text/csv;charset=utf-8" }),
      `applications-${stamp}.csv`
    );

  const exportZip = () =>
    downloadZip(
      {
        "README.txt": [
          "Motisha Sadharmik Bhakti Kendra — Applications export",
          `Generated: ${new Date().toLocaleString("en-IN")}`,
          "",
          "Filters applied",
          `  Search:      ${q || "(none)"}`,
          `  Status:      ${status === "all" ? "All" : status}`,
          `  Category:    ${category === "all" ? "All" : (categories ?? []).find(c => c.id === category)?.name}`,
          `  Date range:  ${from || "start"} to ${to || "today"}`,
          `  Min amount:  ${minAmt || "0"}`,
          "",
          `Records: ${rows.length}`,
          `Total requirement: ${totals.requirement}`,
          `Balance required:  ${totals.balance}`,
        ].join("\n"),
        "applications.csv": csv(),
      },
      `applications-${stamp}.zip`
    );

  const view = rows.find(r => r.id === viewId) ?? null;

  const reset = () => {
    setQ("");
    setStatus("all");
    setCategory("all");
    setFrom("");
    setTo("");
    setMinAmt("");
  };

  return (
    <Shell>
      <PageHeader
        title="All Applications"
        subtitle="Filter, export and page through every recorded application"
        actions={
          <>
            <Button
              variant="outline"
              onClick={exportCsv}
              disabled={!rows.length}
            >
              <Download className="mr-2 h-4 w-4" /> Export CSV
            </Button>
            <Button
              variant="outline"
              onClick={exportZip}
              disabled={!rows.length}
            >
              <FileArchive className="mr-2 h-4 w-4" /> Share ZIP
            </Button>
            <Button asChild>
              <Link to="/applications/new">
                <Plus className="mr-2 h-4 w-4" /> New Application
              </Link>
            </Button>
          </>
        }
      />

      <Card className="mb-4">
        <CardContent className="grid gap-3 p-4 md:grid-cols-3 xl:grid-cols-6">
          <div className="md:col-span-3 xl:col-span-2">
            <Label className="text-xs text-muted-foreground">Search</Label>
            <div className="relative mt-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="Ref no, name, ration card, mobile…"
                value={q}
                onChange={e => setQ(e.target.value)}
              />
            </div>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {STATUSES.map(s => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {(categories ?? []).map(c => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">From date</Label>
            <Input
              type="date"
              className="mt-1"
              value={from}
              onChange={e => setFrom(e.target.value)}
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">To date</Label>
            <Input
              type="date"
              className="mt-1"
              value={to}
              onChange={e => setTo(e.target.value)}
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">
              Min requirement (₹)
            </Label>
            <Input
              type="number"
              className="mt-1"
              placeholder="0"
              value={minAmt}
              onChange={e => setMinAmt(e.target.value)}
            />
          </div>
          <div className="flex items-end">
            <Button variant="ghost" onClick={reset} className="w-full">
              <RotateCcw className="mr-2 h-4 w-4" /> Reset filters
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-3">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs uppercase text-muted-foreground">
              Filtered Applications
            </p>
            <p className="font-display mt-1 text-2xl font-semibold text-primary">
              {rows.length}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs uppercase text-muted-foreground">
              Total Requirement
            </p>
            <p className="font-display mt-1 text-2xl font-semibold text-primary">
              {inr(totals.requirement)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs uppercase text-muted-foreground">
              Balance Required
            </p>
            <p className="font-display mt-1 text-2xl font-semibold text-primary">
              {inr(totals.balance)}
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="data-table w-full text-sm">
              <thead className="bg-muted/60 text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Ref</th>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Applicant</th>
                  <th className="px-4 py-3">Ration Card</th>
                  <th className="px-4 py-3">Category</th>
                  <th className="px-4 py-3 text-right">Requirement</th>
                  <th className="px-4 py-3 text-right">Balance</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paged.map(a => (
                  <tr
                    key={a.id}
                    className="hover:bg-muted/40 border-t border-border"
                  >
                    <td className="px-4 py-2 font-medium">
                      <Link
                        to="/applications/$id"
                        params={{ id: a.id }}
                        className="underline"
                      >
                        {a.ref_no}
                      </Link>
                    </td>
                    <td className="px-4 py-2">
                      {shortDate(a.application_date)}
                    </td>
                    <td className="px-4 py-2">{a.applicant_name}</td>
                    <td className="px-4 py-2">{a.ration_card_no}</td>
                    <td className="px-4 py-2">
                      {a.assistance_categories?.name}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {inr(a.total_requirement)}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {inr(a.balance_required)}
                    </td>
                    <td className="px-4 py-2">
                      <Badge variant="secondary">{a.status}</Badge>
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 text-right">
                      <Button
                        size="icon"
                        variant="ghost"
                        title="View"
                        aria-label={`View ${a.ref_no}`}
                        onClick={() => setViewId(a.id)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" asChild title="Edit">
                        <Link
                          to="/applications/$id"
                          params={{ id: a.id }}
                          aria-label={`Edit ${a.ref_no}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Link>
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        title="Delete"
                        aria-label={`Delete ${a.ref_no}`}
                        className="text-destructive hover:text-destructive"
                        onClick={() => setDeleteId(a.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
                {!paged.length && (
                  <tr>
                    <td
                      colSpan={9}
                      className="px-4 py-8 text-center text-muted-foreground"
                    >
                      {isLoading
                        ? "Loading…"
                        : "No applications match your filters."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <TablePager
            total={rows.length}
            page={page}
            pageSize={pageSize}
            onPage={setPage}
            onPageSize={setPageSize}
          />
        </CardContent>
      </Card>

      <Dialog open={Boolean(view)} onOpenChange={o => !o && setViewId(null)}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {view?.ref_no} — {view?.applicant_name}
            </DialogTitle>
          </DialogHeader>
          {view && (
            <div className="grid gap-3 text-sm md:grid-cols-2">
              <Info
                label="Application Date"
                value={shortDate(view.application_date)}
              />
              <Info label="Status" value={view.status} />
              <Info label="Beneficiary" value={view.beneficiary_name || "—"} />
              <Info label="Ration Card" value={view.ration_card_no || "—"} />
              <Info label="Mobile" value={view.applicant_mobile || "—"} />
              <Info
                label="Category"
                value={view.assistance_categories?.name || "—"}
              />
              <div className="md:col-span-2">
                <Info label="Purpose" value={view.purpose || "—"} />
              </div>
              <Info
                label="Total Requirement"
                value={inr(view.total_requirement)}
              />
              <Info
                label="Applicant Contribution"
                value={inr(view.applicant_contribution)}
              />
              <Info
                label="Trust Contribution"
                value={inr(view.our_contribution)}
              />
              <Info label="Other Received" value={inr(view.other_received)} />
              <Info
                label="Balance Required"
                value={inr(view.balance_required)}
              />
              <Info
                label="Bank"
                value={
                  [view.bank_name, view.bank_branch]
                    .filter(Boolean)
                    .join(", ") || "—"
                }
              />
              <div className="flex gap-2 pt-2 md:col-span-2">
                <Button asChild>
                  <Link to="/applications/$id" params={{ id: view.id }}>
                    <Pencil className="mr-2 h-4 w-4" /> Open &amp; Edit
                  </Link>
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={Boolean(deleteId)}
        onOpenChange={o => !o && setDeleteId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this application?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the application along with its
              institution allocations, documents and generated letter history.
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={busy}
              onClick={async e => {
                e.preventDefault();
                if (!deleteId) {
                  return;
                }
                setBusy(true);
                try {
                  await supabase
                    .from("generated_letters")
                    .delete()
                    .eq("application_id", deleteId);
                  await supabase
                    .from("application_organizations")
                    .delete()
                    .eq("application_id", deleteId);
                  await supabase
                    .from("documents")
                    .delete()
                    .eq("application_id", deleteId);
                  const { error } = await supabase
                    .from("applications")
                    .delete()
                    .eq("id", deleteId);
                  if (error) {
                    throw error;
                  }
                  toast.success("Application deleted");
                  setDeleteId(null);
                  qc.invalidateQueries({ queryKey: ["applications-list"] });
                } catch (err) {
                  toast.error((err as Error).message);
                } finally {
                  setBusy(false);
                }
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Shell>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className="font-medium">{value}</p>
    </div>
  );
}
